import { Popover } from 'antd';
import cn from 'classnames';
import { useLayoutEffect, useMemo, useRef, useState } from 'react';

import { Tag } from '@app/shared/ui/Tag';
import type { TagItemData } from '@app/shared/ui/Tag';

import { getVisibleTagsCount } from '../lib/getVisibleTagsCount';

import type { TagBox } from '../lib/getVisibleTagsCount';
import type { FC } from 'react';

import './TagsOverflowList.scss';

// Kept in sync with the `max-height` guard in TagsOverflowList.scss.
const TAGS_MAX_ROWS = 2;
// Used only for a row that holds a single tag, where there is no in-row pair to
// read the real gap from. Matches the `gap` in TagsOverflowList.scss.
const FALLBACK_GAP_PX = 4;

interface TagsOverflowListProps {
  items: TagItemData[];
  className?: string;
}

const renderTag = (item: TagItemData) => (
  <Tag key={item.id} className="TagsOverflowList__tag" item={item} />
);

const measureGap = (boxes: TagBox[]) => {
  for (let index = 1; index < boxes.length; index++) {
    const box = boxes[index];
    const previousBox = boxes[index - 1];

    if (box.top === previousBox.top) {
      return Math.max(
        0,
        Math.round(box.left - (previousBox.left + previousBox.width))
      );
    }
  }

  return FALLBACK_GAP_PX;
};

/**
 * Tags cell that wraps tags over up to two rows and collapses whatever does not
 * fit into a "+N" popover. Unlike a fixed "show the first N tags" rule, the cut
 * is decided by what actually fits, so short tags are not hidden behind a "+N"
 * while there is still free space next to them.
 *
 * Measuring needs every tag laid out, so the first render draws the full list
 * and the "+N" button with its widest possible label. Both happen inside a
 * layout effect, before the browser paints, so the user never sees that pass.
 * Reserving the widest label also guarantees the real, shorter button always
 * fits into the space that was measured for it.
 */
export const TagsOverflowList: FC<TagsOverflowListProps> = ({
  items,
  className,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const moreButtonRef = useRef<HTMLButtonElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [visibleCount, setVisibleCount] = useState<number | null>(null);

  const itemsKey = useMemo(() => items.map(({ id }) => id).join('|'), [items]);

  useLayoutEffect(() => {
    const container = containerRef.current;

    if (!container) {
      return;
    }

    const updateContainerWidth = () =>
      setContainerWidth((prev) =>
        prev === container.clientWidth ? prev : container.clientWidth
      );

    updateContainerWidth();

    const resizeObserver = new ResizeObserver(updateContainerWidth);
    resizeObserver.observe(container);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  // Anything that changes the packing invalidates the previous measurement.
  useLayoutEffect(() => {
    setVisibleCount(null);
  }, [itemsKey, containerWidth]);

  // Runs only while the measurement is missing: the reset effect above clears
  // it whenever the tags or the available width change, and setting it here
  // makes every later run bail out at the guard.
  useLayoutEffect(() => {
    if (visibleCount !== null || containerWidth <= 0) {
      return;
    }

    const container = containerRef.current;
    const moreButton = moreButtonRef.current;

    if (!container || !moreButton) {
      return;
    }

    const containerRect = container.getBoundingClientRect();
    const boxes: TagBox[] = [];

    for (let index = 0; index < items.length; index++) {
      const tagElement = container.children[index];

      if (!(tagElement instanceof HTMLElement)) {
        return;
      }

      const tagRect = tagElement.getBoundingClientRect();

      boxes.push({
        left: tagRect.left - containerRect.left,
        top: Math.round(tagRect.top - containerRect.top),
        width: tagRect.width,
      });
    }

    setVisibleCount(
      getVisibleTagsCount({
        boxes,
        containerWidth,
        gap: measureGap(boxes),
        maxRows: TAGS_MAX_ROWS,
        moreButtonWidth: moreButton.getBoundingClientRect().width,
      })
    );
  }, [containerWidth, items, visibleCount]);

  if (items.length === 0) {
    return null;
  }

  const isMeasuring = visibleCount === null;
  const resolvedVisibleCount = isMeasuring ? items.length : visibleCount;
  const hiddenCount = items.length - resolvedVisibleCount;

  return (
    <div ref={containerRef} className={cn('TagsOverflowList', className)}>
      {items.slice(0, resolvedVisibleCount).map(renderTag)}
      {(isMeasuring || hiddenCount > 0) && (
        <Popover
          content={
            <div className="TagsOverflowList__popover-list">
              {items.map(renderTag)}
            </div>
          }
          trigger="click"
          placement="bottomLeft"
          arrow={false}
          classNames={{ root: 'TagsOverflowList__popover' }}
        >
          <button
            ref={moreButtonRef}
            type="button"
            className="TagsOverflowList__more"
          >
            +{isMeasuring ? items.length : hiddenCount}
          </button>
        </Popover>
      )}
    </div>
  );
};
